'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'

// =============================================================================
// DATA
// =============================================================================

const insights = [
  {
    id: 1,
    type: 'trend',
    title: 'African Contemporary surges 24% in Q4',
    summary: 'Works by emerging African artists outperforming the broader market, driven by institutional interest.',
    date: '2h ago',
    tier: 'free',
    impact: 'high',
    artists: [
      { name: 'Amoako Boafo', initials: 'AB', color: '#4ECDC4' },
      { name: 'Njideka Akunyili Crosby', initials: 'NC', color: '#F7CAC9' },
    ],
  },
  {
    id: 2,
    type: 'opportunity',
    title: 'Kusama prints trading below estimates',
    summary: 'Recent editions showing 15-20% below-estimate hammers. Buying opportunity before Tokyo retrospective.',
    date: '5h ago',
    tier: 'collector',
    impact: 'high',
    artists: [
      { name: 'Yayoi Kusama', initials: 'YK', color: '#E85D75' },
    ],
  },
  {
    id: 3,
    type: 'alert',
    title: 'Basquiat market approaching correction',
    summary: 'Technical indicators suggest overheating after 3 consecutive record sales. Consider taking profits.',
    date: '1d ago',
    tier: 'gallery',
    impact: 'critical',
    artists: [
      { name: 'Jean-Michel Basquiat', initials: 'JB', color: '#E63946' },
    ],
  },
  {
    id: 4,
    type: 'analysis',
    title: 'Photography market deep dive',
    summary: 'Comprehensive $890M market analysis. Vintage and contemporary segments, top performers.',
    date: '2d ago',
    tier: 'free',
    impact: 'medium',
    artists: [
      { name: 'Andreas Gursky', initials: 'AG', color: '#5D5D5D' },
      { name: 'Cindy Sherman', initials: 'CS', color: '#9B7EBD' },
    ],
  },
  {
    id: 5,
    type: 'trend',
    title: 'Figurative painting revival continues',
    summary: 'Figurative works outpacing abstract 3:1 in recent contemporary sales.',
    date: '3d ago',
    tier: 'collector',
    impact: 'high',
    artists: [
      { name: 'Jenny Saville', initials: 'JS', color: '#D4A373' },
      { name: 'Lynette Yiadom-Boakye', initials: 'LY', color: '#6B5B95' },
    ],
  },
  {
    id: 6,
    type: 'opportunity',
    title: 'Digital art finds floor',
    summary: 'Blue-chip digital artists stabilizing. Beeple, Pak showing renewed interest.',
    date: '3d ago',
    tier: 'collector',
    impact: 'medium',
    artists: [
      { name: 'Beeple', initials: 'BP', color: '#7B68EE' },
    ],
  },
]

// Market movers
const marketMovers = [
  { name: 'Jadé Fadojutimi', initials: 'JF', color: '#6B5B95', change: 156.2, reason: 'Secondary debut' },
  { name: 'Christina Quarles', initials: 'CQ', color: '#F7CAC9', change: 89.4, reason: 'Venice Biennale' },
  { name: 'Amoako Boafo', initials: 'AB', color: '#4ECDC4', change: 42.1, reason: 'MoMA acquisition' },
  { name: 'KAWS', initials: 'KW', color: '#FF69B4', change: -18.4, reason: 'Market saturation' },
  { name: 'Damien Hirst', initials: 'DH', color: '#5D5D5D', change: -12.8, reason: 'NFT underperformance' },
]

// Auction highlights
const auctionHighlights = [
  { title: 'Post-War Evening', house: "Christie's", houseColor: '#E63946', date: 'Mar 12', topLot: 'Basquiat', estimate: '$40-60M' },
  { title: 'Impressionist & Modern', house: "Sotheby's", houseColor: '#4A90D9', date: 'Mar 14', topLot: 'Monet', estimate: '$35-50M' },
  { title: '20th Century Art', house: 'Phillips', houseColor: '#7B68EE', date: 'Mar 20', topLot: 'Hockney', estimate: '$15-20M' },
]

// Geographic
const geographicHotspots = [
  { region: 'Hong Kong', trend: 22, highlight: 'Asian Modern surge' },
  { region: 'Dubai', trend: 18, highlight: 'Contemporary growth' },
  { region: 'New York', trend: 8, highlight: 'Blue-chip stability' },
  { region: 'London', trend: 5, highlight: 'Old Masters recovery' },
]

const typeConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  trend: {
    color: 'var(--positive)',
    label: 'Trend',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />,
  },
  opportunity: {
    color: 'var(--accent-primary)',
    label: 'Opportunity',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
  },
  alert: {
    color: 'var(--negative)',
    label: 'Alert',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />,
  },
  analysis: {
    color: 'var(--accent-secondary)',
    label: 'Analysis',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
  },
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function InsightsPage() {
  const { user } = useAuth()
  const userTier = user?.tier || 'free'
  const isFreeUser = userTier === 'free'
  const [activeFilter, setActiveFilter] = useState<string>('all')

  const tierOrder = ['free', 'collector', 'gallery', 'enterprise']
  const userTierIndex = tierOrder.indexOf(userTier)

  const filteredInsights = useMemo(() => {
    if (activeFilter === 'all') return insights
    return insights.filter(i => i.type === activeFilter)
  }, [activeFilter])

  const counts = {
    trends: insights.filter(i => i.type === 'trend').length,
    opportunities: insights.filter(i => i.type === 'opportunity').length,
    alerts: insights.filter(i => i.type === 'alert').length,
    analyses: insights.filter(i => i.type === 'analysis').length,
  }

  return (
    <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-2xl md:text-3xl font-light">Insights</h1>
            <span className="text-xs px-2 py-1 rounded-full bg-accent-primary/10 text-accent-primary">{insights.length} new</span>
          </div>
          <p className="text-text-secondary text-sm">AI-powered market intelligence</p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'trend', 'opportunity', 'alert', 'analysis'].map(f => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all capitalize ${
                activeFilter === f
                  ? 'bg-text-primary text-white'
                  : 'border border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              {f === 'all' ? 'All' : f}s
            </button>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="card p-3 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-positive" />
          <div>
            <p className="text-lg font-display font-light">{counts.trends}</p>
            <p className="text-xs text-text-tertiary">Trends</p>
          </div>
        </div>
        <div className="card p-3 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-primary)' }} />
          <div>
            <p className="text-lg font-display font-light">{counts.opportunities}</p>
            <p className="text-xs text-text-tertiary">Opportunities</p>
          </div>
        </div>
        <div className="card p-3 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-negative" />
          <div>
            <p className="text-lg font-display font-light">{counts.alerts}</p>
            <p className="text-xs text-text-tertiary">Alerts</p>
          </div>
        </div>
        <div className="card p-3 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-secondary)' }} />
          <div>
            <p className="text-lg font-display font-light">{counts.analyses}</p>
            <p className="text-xs text-text-tertiary">Analyses</p>
          </div>
        </div>
      </div>

      {/* Two Column: Insights Feed + Sidebar */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Feed */}
        <div className="lg:col-span-2 space-y-4">
          {filteredInsights.map((insight, i) => {
            const config = typeConfig[insight.type]
            const insightTierIndex = tierOrder.indexOf(insight.tier)
            const isLocked = insightTierIndex > userTierIndex

            return (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`card p-4 border-l-2 ${isLocked ? 'opacity-60' : ''}`}
                style={{ borderLeftColor: config.color }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `color-mix(in srgb, ${config.color} 15%, transparent)` }}
                  >
                    <svg className="w-4 h-4" style={{ color: config.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {config.icon}
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: `color-mix(in srgb, ${config.color} 15%, transparent)`, color: config.color }}>
                        {config.label}
                      </span>
                      {insight.impact === 'critical' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-negative/10 text-negative">Critical</span>
                      )}
                      {insight.impact === 'high' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-accent-warm/10 text-accent-warm">High Impact</span>
                      )}
                      <span className="text-xs text-text-tertiary ml-auto">{insight.date}</span>
                    </div>
                    <h3 className="font-medium text-sm mb-1">{insight.title}</h3>
                    <p className={`text-xs text-text-secondary mb-2 ${isLocked ? 'blur-[4px]' : ''}`}>{insight.summary}</p>

                    {/* Related artists */}
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-1">
                        {insight.artists.slice(0, 3).map((artist, j) => (
                          <div
                            key={j}
                            className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-medium border-2 border-card"
                            style={{ background: artist.color }}
                            title={artist.name}
                          >
                            {artist.initials}
                          </div>
                        ))}
                      </div>
                      <span className="text-xs text-text-tertiary">
                        {insight.artists.map(a => a.name).join(', ')}
                      </span>
                    </div>

                    {isLocked && (
                      <Link href="/get-started" className="text-xs text-accent-primary hover:underline mt-2 inline-block">
                        Upgrade to {insight.tier} →
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Market Movers */}
          <div className="card p-4">
            <h2 className="font-medium text-sm mb-3">Market Movers</h2>
            <div className="space-y-2">
              {marketMovers.slice(0, 4).map((artist) => (
                <div key={artist.name} className="flex items-center gap-2 py-1.5">
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                    style={{ background: artist.color }}
                  >
                    {artist.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{artist.name}</p>
                    <p className="text-xs text-text-tertiary">{artist.reason}</p>
                  </div>
                  <span className={`text-xs font-medium tabular-nums ${artist.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {artist.change >= 0 ? '+' : ''}{artist.change}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Auction Highlights */}
          <div className="card p-4">
            <h2 className="font-medium text-sm mb-3">Upcoming Auctions</h2>
            <div className="space-y-2">
              {auctionHighlights.map((auction) => (
                <div key={auction.title} className="py-1.5">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: `${auction.houseColor}15`, color: auction.houseColor }}>
                      {auction.house}
                    </span>
                    <span className="text-xs text-text-tertiary">{auction.date}</span>
                  </div>
                  <p className="text-sm font-medium">{auction.topLot}</p>
                  <p className="text-xs text-text-tertiary">Est. {auction.estimate}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Geographic */}
          <div className="card p-4">
            <h2 className="font-medium text-sm mb-3">Geographic Hotspots</h2>
            <div className="space-y-2">
              {geographicHotspots.map((geo) => (
                <div key={geo.region} className="flex items-center justify-between py-1.5">
                  <div>
                    <p className="text-sm font-medium">{geo.region}</p>
                    <p className="text-xs text-text-tertiary">{geo.highlight}</p>
                  </div>
                  <span className="text-xs font-medium text-positive tabular-nums">+{geo.trend}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upgrade CTA */}
          {isFreeUser && (
            <div className="card p-4 bg-gradient-to-br from-accent-primary/5 to-transparent border-accent-primary/20">
              <h3 className="font-medium text-sm mb-1">Access all insights</h3>
              <p className="text-xs text-text-secondary mb-3">Get alerts, opportunities, and AI analysis.</p>
              <Link href="/get-started" className="block w-full text-center px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: 'var(--accent-primary)' }}>
                From $199/mo
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
